import { BunkerAudioFilter } from '@bunker/audio';
import { getAudioContextCtor } from './audioContext';
import { loadRnnoise, RnnoiseWorkletNode } from '@sapphi-red/web-noise-suppressor';
import rnnoiseWorkletUrl from '@sapphi-red/web-noise-suppressor/rnnoiseWorklet.js?url';
import rnnoiseWasmUrl from '@sapphi-red/web-noise-suppressor/rnnoise.wasm?url';
import rnnoiseSimdWasmUrl from '@sapphi-red/web-noise-suppressor/rnnoise_simd.wasm?url';

/**
 * Builds the local microphone processing graph that sits between the raw
 * getUserMedia track and what we actually send to peers / the SFU:
 *
 *   rawMicTrack → MediaStreamAudioSourceNode
 *     → [«Как в бункере» (BunkerAudioFilter)]   — our proprietary DSP
 *     → [Нейрошумодав (RNNoise)]                — added in a later phase
 *     → [input GainNode]                         — 0–200% microphone volume
 *     → MediaStreamAudioDestinationNode → processed track
 *
 * Each stage is independently toggleable (user owns the choice). When no
 * stage is enabled the caller sends the raw track directly — we don't build
 * a graph at all, so the zero-filter path stays bit-for-bit identical to
 * before this module existed.
 *
 * Muting is handled upstream by disabling the RAW track (`track.enabled =
 * false`): a disabled source feeds silence into the graph, so the processed
 * output is silent too — no special-casing needed here.
 */

export interface AudioGraphStages {
  /** «Как в бункере» — our DSP chain. */
  bunker: boolean;
  /** Нейрошумодав — RNNoise (ML noise suppression). */
  rnnoise: boolean;
  /** Премиум-нейрошумодав — DeepFilterNet (heavier ML, lazy-loaded ~16MB). */
  dfnet: boolean;
  /** Global microphone volume differs from 100% and needs a GainNode. */
  gain: boolean;
}

export interface ProcessedAudio {
  /** The processed track to produce / send instead of the raw mic track. */
  track: MediaStreamTrack;
  /** Enable/disable the graph's input (used for mute) — toggles the cloned
   *  source track, since the processed track is what's actually sent. */
  setEnabled: (enabled: boolean) => void;
  /** Tear down all nodes + the cloned source track. Does not stop the raw
   *  track (caller owns it). */
  dispose: () => void;
  /** Live-tune the DeepFilterNet noise-suppression intensity (attenuation
   *  limit in dB, 0..100). No-op when the dfnet stage isn't in the graph.
   *  Avoids a full graph rebuild when only the intensity slider moves. */
  setDfnetAtten: (limDb: number) => void;
  /** Change microphone gain without rebuilding the graph or replacing the SFU
   * track. The preference range is 0–200%; 100% is unchanged. */
  setInputGain: (percent: number) => void;
  /** Whether the shared AudioContext was actually `running` when this graph
   *  finished building. A graph built on a `suspended` context (autoplay
   *  policy — the join click's activation was spent on the socket round-trip
   *  + the mic permission prompt) produces a live-but-SILENT track; the
   *  caller must arm a recovery rebuild via {@link onAudioContextRunning}. */
  contextRunning: boolean;
}

export function anyStageEnabled(stages: AudioGraphStages): boolean {
  return stages.bunker || stages.rnnoise || stages.dfnet || stages.gain;
}

// One shared AudioContext for local mic processing. Reused across rebuilds so
// we don't leak contexts (browsers cap how many you can create).
let sharedContext: AudioContext | null = null;

// Subscribers waiting for the shared context to reach `running` (used by
// voiceMedia to rebuild + hot-swap a silent production track built while the
// context was suspended). Fired from the context's own `statechange`.
const runningListeners = new Set<() => void>();

function notifyRunning() {
  for (const cb of [...runningListeners]) cb();
}

/**
 * Subscribe to "the shared mic-processing context just became running".
 * Returns an unsubscribe. Fires on every suspended→running transition.
 */
export function onAudioContextRunning(cb: () => void): () => void {
  runningListeners.add(cb);
  return () => runningListeners.delete(cb);
}

/** Current state of the shared context, or null if it doesn't exist yet. */
export function getSharedAudioContextState(): AudioContextState | null {
  return sharedContext && sharedContext.state !== 'closed' ? sharedContext.state : null;
}

/**
 * Create/resume the shared context. MUST be called synchronously inside a
 * user-gesture handler (the voice-join click) — that is the whole point:
 * by the time the mic graph is built, the click's transient activation has
 * been spent on the `voice:join` socket round-trip and (for first-time
 * users) the mic permission prompt, so `resume()` there is too late and the
 * context stays `suspended` → the processed track goes out SILENT. Resuming
 * here, inside the click itself, is what makes a first join audible.
 */
export function primeAudioContext(): void {
  try {
    const context = getContext();
    if (context.state !== 'running') {
      void context.resume().catch(() => {});
    }
  } catch {
    // No AudioContext in this environment (tests / very old browser) — the
    // zero-filter raw-track path doesn't need it.
  }
}

// Last-resort recovery: when a graph got built on a suspended context, any
// later user interaction is a valid activation — resume on the next one.
let gestureResumeArmed = false;
function armGestureResume(context: AudioContext): void {
  if (gestureResumeArmed) return;
  gestureResumeArmed = true;
  const handler = () => {
    document.removeEventListener('pointerdown', handler, true);
    document.removeEventListener('keydown', handler, true);
    gestureResumeArmed = false;
    void context.resume().catch(() => {});
  };
  document.addEventListener('pointerdown', handler, { capture: true });
  document.addEventListener('keydown', handler, { capture: true });
}

function getContext(): AudioContext {
  if (!sharedContext || sharedContext.state === 'closed') {
    const Ctor = getAudioContextCtor();
    // Force 48 kHz: both RNNoise and DeepFilterNet are trained for 48 kHz and
    // expect 480-sample (10 ms) hops. On hardware whose default rate is 44.1
    // kHz the model would receive off-rate audio and produce a watery /
    // artefacted result. The mic source is resampled into the context rate by
    // createMediaStreamSource, and Opus is 48 kHz native, so 48 kHz is the
    // correct, lossless choice for the whole graph.
    try {
      sharedContext = new Ctor({ latencyHint: 'interactive', sampleRate: 48000 });
    } catch {
      // Extremely rare: a browser that rejects an explicit sampleRate. Fall
      // back to the hardware default rather than failing to build any graph.
      sharedContext = new Ctor({ latencyHint: 'interactive' });
    }
    const created = sharedContext;
    created.addEventListener('statechange', () => {
      if (created.state === 'running') notifyRunning();
    });
  }
  return sharedContext;
}

// RNNoise wasm is fetched once and reused; the worklet module is added once
// per AudioContext.
let rnnoiseWasm: ArrayBuffer | null = null;
const rnnoiseReadyContexts = new WeakSet<BaseAudioContext>();

async function ensureRnnoise(context: AudioContext): Promise<void> {
  if (!rnnoiseWasm) {
    rnnoiseWasm = await loadRnnoise({ url: rnnoiseWasmUrl, simdUrl: rnnoiseSimdWasmUrl });
  }
  if (!rnnoiseReadyContexts.has(context)) {
    await context.audioWorklet.addModule(rnnoiseWorkletUrl);
    rnnoiseReadyContexts.add(context);
  }
}

// DeepFilterNet («Премиум»). Heavy (~16MB wasm+model) — fetched lazily the
// first time the user enables it, then cached. Assets live in public/dfnet
// (served same-origin, allowed by the strict CSP). The worklet file bundles
// the no-modules wasm-bindgen glue + the processor.
const DFNET_WASM_URL = '/dfnet/df_bg.wasm';
const DFNET_MODEL_URL = '/dfnet/model.tar.gz';
const DFNET_WORKLET_URL = '/dfnet/dfnet-worklet.js';
interface DfnetAssets {
  wasmModule: WebAssembly.Module;
  modelBytes: ArrayBuffer;
}
let dfnetAssets: DfnetAssets | null = null;
let dfnetAssetsPromise: Promise<DfnetAssets> | null = null;
const dfnetReadyContexts = new WeakSet<BaseAudioContext>();

async function fetchBuffer(url: string): Promise<ArrayBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${url} → HTTP ${res.status}`);
  return res.arrayBuffer();
}

async function ensureDfnet(context: AudioContext): Promise<void> {
  if (!dfnetAssets) {
    if (!dfnetAssetsPromise) {
      dfnetAssetsPromise = (async () => {
        const [wasmBuf, modelBuf] = await Promise.all([
          fetchBuffer(DFNET_WASM_URL),
          fetchBuffer(DFNET_MODEL_URL),
        ]);
        return { wasmModule: await WebAssembly.compile(wasmBuf), modelBytes: modelBuf };
      })();
      // If the lazy fetch/compile fails (offline, 404, bad wasm), drop the
      // cached promise so a later toggle can retry instead of being stuck on
      // the same rejected promise forever.
      dfnetAssetsPromise.catch(() => {
        dfnetAssetsPromise = null;
      });
    }
    dfnetAssets = await dfnetAssetsPromise;
  }
  if (!dfnetReadyContexts.has(context)) {
    await context.audioWorklet.addModule(DFNET_WORKLET_URL);
    dfnetReadyContexts.add(context);
  }
}

/**
 * Build the processing graph for `rawStream` with the given stages enabled.
 * Returns `null` when there's no audio track or no stage is enabled (caller
 * should fall back to the raw track).
 */
export async function buildProcessedAudio(
  rawStream: MediaStream,
  stages: AudioGraphStages,
  options: { dfnetAttenLim?: number; inputGain?: number } = {},
): Promise<ProcessedAudio | null> {
  const rawTrack = rawStream.getAudioTracks()[0] ?? null;
  if (!rawTrack || !anyStageEnabled(stages)) return null;

  // DeepFilterNet attenuation limit (dB). Clamped to a sane 0..100 range.
  const dfnetAttenLim = Math.max(0, Math.min(100, options.dfnetAttenLim ?? 100));
  const inputGain = Math.max(0, Math.min(2, (options.inputGain ?? 100) / 100));

  const context = getContext();
  // Try to resume — but on a FIRST join the click's transient activation is
  // usually already spent (socket round-trip + mic permission prompt happen
  // before we get here), so this can silently fail and leave the context
  // suspended → the destination outputs silence. primeAudioContext() (called
  // inside the join click) is the real fix; the gesture listener below is the
  // last-resort recovery for anything that slips through.
  await context.resume().catch(() => {});
  if (context.state !== 'running') armGestureResume(context);

  // Feed the graph a CLONE of the mic track, not the original. A track that
  // is already attached to the WebRTC sender (the SFU producer) outputs
  // SILENCE when piped into a MediaStreamAudioSourceNode (long-standing
  // Chromium behaviour) — which silently killed audio when a filter was
  // enabled mid-call. The clone is an independent feed of the same device.
  const srcTrack = rawTrack.clone();
  srcTrack.enabled = rawTrack.enabled;
  const source = context.createMediaStreamSource(new MediaStream([srcTrack]));
  let node: AudioNode = source;
  const cleanups: Array<() => void> = [() => stopTrack(srcTrack)];
  // Captured so setDfnetAtten can live-tune intensity without a rebuild.
  let dfnetPort: MessagePort | null = null;

  // Order: ML denoise first (DeepFilterNet / RNNoise remove noise), then our
  // DSP shapes / gates / levels the cleaned signal.
  if (stages.dfnet) {
    await ensureDfnet(context);
    const dfnetNode = new AudioWorkletNode(context, 'dfnet', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount: 1,
      channelCountMode: 'explicit',
      channelInterpretation: 'speakers',
      processorOptions: {
        wasmModule: dfnetAssets!.wasmModule,
        modelBytes: dfnetAssets!.modelBytes,
        attenLim: dfnetAttenLim,
      },
    });
    dfnetPort = dfnetNode.port;
    node.connect(dfnetNode);
    node = dfnetNode;
    cleanups.push(() => safeDisconnect(dfnetNode));
  }

  if (stages.rnnoise) {
    await ensureRnnoise(context);
    const rnnoise = new RnnoiseWorkletNode(context, { maxChannels: 1, wasmBinary: rnnoiseWasm! });
    node.connect(rnnoise);
    node = rnnoise;
    cleanups.push(() => safeDisconnect(rnnoise));
  }

  if (stages.bunker) {
    await BunkerAudioFilter.register(context);
    const filter = new BunkerAudioFilter(context);
    node.connect(filter.input);
    node = filter.output;
    cleanups.push(() => filter.destroy());
  }

  const destination = context.createMediaStreamDestination();
  // Preserve the old zero-overhead path when every filter is off and the
  // volume is at its neutral 100%. A gain node exists only while the input
  // slider actually needs attenuation or amplification.
  const gain = stages.gain ? context.createGain() : null;
  if (gain) {
    gain.gain.value = inputGain;
    node.connect(gain).connect(destination);
  } else {
    node.connect(destination);
  }
  const track = destination.stream.getAudioTracks()[0] ?? null;

  if (!track) {
    for (const c of cleanups) c();
    safeDisconnect(source);
    return null;
  }

  return {
    track,
    setEnabled: (enabled: boolean) => {
      srcTrack.enabled = enabled;
    },
    dispose: () => {
      for (const c of cleanups) c();
      safeDisconnect(source);
      if (gain) safeDisconnect(gain);
      safeDisconnect(destination);
    },
    setDfnetAtten: (limDb: number) => {
      if (!dfnetPort) return;
      dfnetPort.postMessage({ type: 'setAttenLim', value: Math.max(0, Math.min(100, limDb)) });
    },
    setInputGain: (percent: number) => {
      if (gain) gain.gain.value = Math.max(0, Math.min(2, percent / 100));
    },
    contextRunning: context.state === 'running',
  };
}

function safeDisconnect(node: AudioNode) {
  try {
    node.disconnect();
  } catch {
    /* already detached */
  }
}

function stopTrack(track: MediaStreamTrack) {
  try {
    track.stop();
  } catch {
    /* already stopped */
  }
}
